/**
 * Voice Memo Prototype Mock Data
 * Mock data generator for testing the voice memo prototype UI
 */

import type { VoiceMemo, VoiceMemoPackage } from './types';

// Mock data generator using REAL examples from archive
export function generateMockData(): VoiceMemo[] {
  return [
    // Package 1: 2025-10-17 evening session (grouped - all within 1 hour)
    {
      id: 'vm-1',
      filename: '20251017 173800-8690E0BC.m4a',
      title: 'London Trip Debrief - Family Visit and Conference',
      duration: '1:10',
      date: '2025-10-17',
      timestamp: '173800',
      status: 'analyzed',
      fileSize: '0.58MB',
      theme: 'personal', // Dynamic theme - example
      summary: 'Debrief about trip to London, UK including two days in Reading with family and conference attendance.',
      wordCount: 125,
      transcript: {
        path: 'inbox/voice-memos/20251017 173800-8690E0BC.md',
        content: `---
type: voice-memo
date: 2025-10-18
duration: 1:10
word_count: 125
transcription_service: OpenAI Whisper
original_file: 20251017 173800-8690E0BC.m4a
confidence: 73.7%

# FlowNote workflow tracking
workflow:
  current_stage: reviewed
  stages_completed: ["transcribed", "reviewed"]
  stages_available: ["filed"]

# Voice Memo Transcript - 2025-10-18

## Raw Transcript

All right, first voice memo after getting home from London, UK. I guess just a little debrief about that trip. Really awesome overall, really happy with how it went. Two days in Reading with an ant-type figure, Claire, on my mum's side, who is her Helen's mum Phyllis's father's, I guess, or Phyllis's sister, maybe, and below. So sort of on the Phyllis side of things. I'm gapping on the last name right now, but it starts with a B or something. Shoot. Anyway, got to remember that. Look that one up. But some family tree stuff, learned about all sorts of things related to my mum's side, which is a lot of stuff I never knew. Met Claire's son Alex and another cousin-type figure.

## Auto-detected References
- No automatic project references detected

## Suggested Actions
- Manual review recommended for content categorization`
      },
      packageId: 'pkg-2025-10-17'
    },
    {
      id: 'vm-2',
      filename: '20251017 173816-8690E0BD.m4a',
      duration: '1:02',
      date: '2025-10-17',
      timestamp: '173816',
      status: 'transcribed',
      fileSize: '0.52MB',
      transcript: {
        path: 'inbox/voice-memos/20251017 173816-8690E0BD.md',
        content: `---
type: voice-memo
date: 2025-10-18
duration: 1:02
word_count: 134
transcription_service: OpenAI Whisper
original_file: 20251017 173816-8690E0BD.m4a
confidence: 69.6%

# Voice Memo Transcript - 2025-10-18

## Raw Transcript

Got cut off there. Yeah, so Alex and what was the other guy's name? Not Michael. Oh wow, I can't believe I can't remember. Okay, well I gotta review my notes on who I visited because that was a nice visit. We had a nice dinner at an Indian place and then London for the next three, four, or I guess five nights, because yeah, two nights at an Airbnb and then in the hotel for three nights. Yeah, all in all really good. I enjoyed London a lot. So many memories and just so cool to be around and people watch and culture watch and look at the scenery and the buildings and the architecture and really fun. The conference itself was quite good. Kind of, I guess a little conference review next.`
      },
      wordCount: 134,
      packageId: 'pkg-2025-10-17',
      theme: 'personal' // Dynamic theme - example
    },
    {
      id: 'vm-3',
      filename: '20251017 173921-4BA85265.m4a',
      duration: '3:11',
      date: '2025-10-17',
      timestamp: '173921',
      status: 'transcribed',
      fileSize: '3.11MB',
      transcript: {
        path: 'inbox/voice-memos/20251017 173921-4BA85265.md',
        content: `---
type: voice-memo
date: 2025-10-18
duration: 3:11
word_count: 450
transcription_service: OpenAI Whisper
original_file: 20251017 173921-4BA85265.m4a

# Voice Memo Transcript - 2025-10-18

## Raw Transcript

[Transcript content from 20251017 173921-4BA85265.md]`
      },
      wordCount: 450,
      packageId: 'pkg-2025-10-17',
      theme: 'personal' // Dynamic theme - example
    },
    {
      id: 'vm-4',
      filename: '20251017 174540-4711FB55.m4a',
      duration: '2:96',
      date: '2025-10-17',
      timestamp: '174540',
      status: 'transcribed',
      fileSize: '2.96MB',
      transcript: {
        path: 'inbox/voice-memos/20251017 174540-4711FB55.md',
        content: `---
type: voice-memo
date: 2025-10-18
duration: 2:96
word_count: 380
transcription_service: OpenAI Whisper
original_file: 20251017 174540-4711FB55.m4a

# Voice Memo Transcript - 2025-10-18

## Raw Transcript

[Transcript content from 20251017 174540-4711FB55.md]`
      },
      wordCount: 380,
      packageId: 'pkg-2025-10-17',
      theme: 'personal' // Dynamic theme - example
    },
    // Package 2: 2025-11-30 session (grouped by date)
    {
      id: 'vm-5',
      filename: 'New Recording 41.m4a',
      title: 'FlowNotes Development Update - Meetings and Feature Work',
      duration: '3:36',
      date: '2025-11-30',
      timestamp: '120000',
      status: 'transcribed',
      fileSize: '1.77MB',
      wordCount: 544,
      packageId: 'pkg-2025-11-30',
      theme: 'work', // Dynamic theme - example
      transcript: {
        path: 'inbox/voice-memos/New Recording 41.md',
        content: `---
type: voice-memo
date: 2025-11-30
duration: 3:36
word_count: 544
transcription_service: OpenAI Whisper
original_file: New Recording 41.m4a
confidence: 74.0%

# Voice Memo Transcript - 2025-11-30

## Raw Transcript

I had a big week with the app currently known as flow notes doing some meetings and lots of feature work and lots of new development a lot of ideas and things coming through for features and I talked to Packet Tide CEO and yeah we had a good talk about lots of different things showed him a little bit about the app and just the concept and talked about the infrastructure layer and the plugging it into AI and that type of stuff so that was nice and and then talked to Seamus just yesterday on Friday about business side of things and startup stuff and grants and money and fundraising and venture capitalists all sorts of things like that he didn't see the app but he gets it he understands the excitement about it and so he's wants to introduce me to some people and have some conversations so that'll be interesting I'm having quite a bit of progress with the interface and the the AI chat features I'm sort of building out all these different pieces of that for editing and viewing what you've changed and reviewing changes all that type of stuff so pretty cool to be building at the speed I'm building I am finding that some of the like UI elements I'm maybe getting a little too zeroed in on like I'm a little too crude I mean not crude sorry I'm a little too like fine at this point by focusing on those but there's some little elements that I'm just like oh no that's not right the way I did it last time or the way that guy built was not right I'm trying to fix it and so it's very satisfying but it I've noticed it takes the longest it's the most cumbersome and often you need the most prompts to tweak it stuff with front-end and CSS so that's kind of a bit of a problem I have been experimenting with the platform called v0 I guess it's short for version zero it's a AI design and prototyping tool so that's been neat to like build that out and then I've also kind of yeah a couple features I've messed around and with there but it's I'm not paying for that service so I'm just kind of limited by the a certain number of requests for a day or something but it's amazing what you can get with one really solid request like I tried like a full feature spec design sort of document and instructions and screenshots and everything and it was okay like it did okay I think with refinement and with money it would probably do better but so I've been kind of exploring workflows with like oh here this little aspect I want this to be better how do I you know I like this version that this company did in their chat and I want to copy that and how do we kind of unify that a little bit so that's exciting for sure to explore all that but yeah a little slow that the design front-end stuff is a bit slow`
      }
    },
    {
      id: 'vm-6',
      filename: 'Malaspina Rd 27.m4a',
      title: 'Basecamp Integration and AI-Driven Agency Workflow Ideas',
      duration: '5:23',
      date: '2025-11-30',
      timestamp: '120500',
      status: 'transcribed',
      fileSize: '2.68MB',
      wordCount: 821,
      packageId: 'pkg-2025-11-30',
      theme: 'work', // Dynamic theme - example
      transcript: {
        path: 'inbox/voice-memos/Malaspina Rd 27.md',
        content: `---
type: voice-memo
date: 2025-11-30
duration: 5:23
word_count: 821
transcription_service: OpenAI Whisper
original_file: Malaspina Rd 27.m4a
confidence: 75.2%

# Voice Memo Transcript - 2025-11-30

## Raw Transcript

So a couple features that I was thinking about let's see if I can pull them out of the memory here. One had to do with Basecamp and I'm just experimenting with just like the thought process of myself managing a digital AI driven agency basically and I'm the human in the loop for a dozen different clients that I'll kind of get their work pushed ahead and I do the review and the context alignment and all that stuff so not like replacing humans but replacing coders you know and I think there's a real potential right now with flow notes app to essentially get triggers for oh this to-do exists it's assigned to me it doesn't have a proper to-do description it's unclear what the next steps are and basically make recommendations based on gathering the context so essentially autonomously like working in the behind the scenes it's scheduling these tasks it's like oh here's the task there's like a hundred of these to-dos that I have on my list I want an assessment of every single one of them I want to know what the next steps are I want to know what the blockers are I want to summarize it I want to get it basically like caught up in the database so that all this assessment is smartly done using AI to ingest just like I've been doing with my whole workflow already exists to like pull in the Basecamp in the comments and the search the project files and search for other things like there's it's a pretty straightforward workflow and I have all the data sitting there hypothetically to like help with this right but the cumbersome part is kind of like opening each one up and clicking on the ingest thing and then prompting it to like do the thing you know like that kind of batch job really is what it is it's a batch job of flows that I've built so I can design a flow test it on a few it's like a little program it's like a little script basically so how do we let the user on the like cloud app basically come up with these skills essentially like anthropic skills and put them into a batch process like you got to be able to write code and store that somewhere and run it so how do you do that on a cloud server and how do you do that safely without fucking stuff up and how do you kind of like build that layer of environments you know because you really almost want to spool up a little environment for your agent to work in to kind of do the batching because right now I'm doing that locally with cursor which is great and it works really well but how do you scale that for less technical people or is that even necessary I guess like I'm seeing this layer of infrastructure as like a it's like a workbench it's like a system it's like a integrator platform and I know that there's other tools that are already doing this so I gotta really like research I don't want to try to like go head-to-head with whatever like Google's platform but maybe I can connect to it you know I mean like maybe I can build a connection in so that the instructions and the like soft side of like gathering information and building documents to build the thing so those can be portable and shipped off to other agents to do different prompts so you can have different tools that you interface with like cloud code like whatever Google Firebase or whatever their new AI building platform is and I guess in the simplest way you could just have a markdown file with a spec in it right or a directory with like five or six files so like in a way you kind of just build project files and that's already there so like maybe what I'm talking about isn't necessary but like that sort of comes with I think there's like potential there like I guess the question is does a like maybe the requests come in from users for like oh I want this and this and this I want to be able to do this you know let's design it and maybe that implementation of like oh here's a new feature or a new integration skill that is suggested by X Y & Z you got to make sure it's safe make sure it's not gonna screw anything up and then you can approve it and maybe it goes into a marketplace I had this idea yeah like kind of put things almost like github open source stuff you know like you have this little workflow I think Zapier's got something similar like all these recipes or zaps that are like templates`
      }
    },
    {
      id: 'vm-7',
      filename: 'Malaspina Rd 28.m4a',
      duration: '3:18',
      date: '2025-11-30',
      timestamp: '121000',
      status: 'unprocessed',
      fileSize: '1.65MB',
      packageId: 'pkg-2025-11-30',
      theme: 'work' // Dynamic theme - example
    },
    {
      id: 'vm-8',
      filename: 'Malaspina Rd 29.m4a',
      duration: '3:31',
      date: '2025-11-30',
      timestamp: '121500',
      status: 'transcribed',
      fileSize: '1.75MB',
      packageId: 'pkg-2025-11-30',
      theme: 'work', // Dynamic theme - example
      transcript: {
        path: 'inbox/voice-memos/Malaspina Rd 29.md',
        content: `---
type: voice-memo
date: 2025-11-30
duration: 3:31
word_count: 536
transcription_service: OpenAI Whisper
original_file: Malaspina Rd 29.m4a

# Voice Memo Transcript - 2025-11-30

## Raw Transcript

[Transcript content from Malaspina Rd 29.md]`
      }
    },
    {
      id: 'vm-9',
      filename: 'Malaspina Rd 30.m4a',
      title: 'MCP Methods and AI Agent Skills Discussion',
      duration: '3:45',
      date: '2025-11-30',
      timestamp: '122000',
      status: 'transcribed',
      fileSize: '1.87MB',
      wordCount: 532,
      packageId: 'pkg-2025-11-30',
      theme: 'work', // Dynamic theme - example
      transcript: {
        path: 'inbox/voice-memos/Malaspina Rd 30.md',
        content: `---
type: voice-memo
date: 2025-11-30
duration: 3:45
word_count: 532
transcription_service: OpenAI Whisper
original_file: Malaspina Rd 30.m4a

# Voice Memo Transcript - 2025-11-30

## Raw Transcript

I'm thinking about MCP methods and stuff that agents have access to and so I've built a bunch of these to provide access to Basecamp data, to note data, searching, querying, that type of stuff. Of course there's like file creation, file editing, all that stuff needs to be fleshed out more, but I think there's a good foundation there. So I guess my question is like, is it the system prompt that goes with the request, like the rules? My concern is that there's just too much to do. There's too many options, there's too many different skills. So how does an agent get trained to be efficient and know what actions to take in certain requests or to narrow down the specific request type? You know, like I think there's something to be said about just buttons to do something, right? Like you, you, instead of a chat, you're basically like, you're essentially giving a prompt, but it's like a structured prompt. It's like a structured prompt with a structured, it's like a function call rather than it being a bit like, hey, do this thing because I want to know this and that and the other, which is very subjective and prone to potential confusion, or it might just do something unexpected. And so I think that's what the skills are supposed to be about. So like maybe that's it. Maybe I use the skills protocol to kind of give real structured guardrails to the requests and unify what's coming out of those requests in a more in-depth way. So like, that would be like, I click a button, like update work spec, and it would essentially know exactly what to do. It would have a systematic flow. There's a function getting run that is, has an AI piece in it, but it's not like a chat where you'd be like, oh, I wanted this and the other, you know, figure out what to do. It's like, how do you like, it's like structured data. It's more explicit in the request. And I don't know how that looks eventually. Is that just like more context in the system prompt that it sort of layers to do that? Like, how does that actually work on AI end of things? Because having some kind of summarization skill could be relatively straightforward. How do you keep it focused and efficient and lean? So yeah, that's kind of what I'm thinking about. And I'm wondering about these local models too. If there's a way to do that. I want to experiment with like local models doing that type of basic processing and summarization steps, you know. I think that's, yeah, that's sort of the next thing I want to test out, or one of the next things. Right now I'm just relying on the cloud models to do it all, just for speed and iterating. And it's just sort of easier at this point. But I haven't yet really got the LLAMA connection running or got these other tools running efficiently yet. Local LLMs, that is. So that's interesting. The other interesting thread from my call with Seamus. Let's see. My timing here.`
      }
    },
    {
      id: 'vm-10',
      filename: 'Malaspina Rd 31.m4a',
      duration: '2:59',
      date: '2025-11-30',
      timestamp: '122500',
      status: 'unprocessed',
      fileSize: '1.49MB',
      packageId: 'pkg-2025-11-30',
      theme: 'work' // Dynamic theme - example
    }
  ];
}
